import React, { useState, useEffect } from 'react';
import { 
  Calendar, Package, TrendingUp, Download, Filter,
  FileText, Search, BarChart2, Clock, CheckCircle
} from 'lucide-react';
import { supabase } from '@/config/supabase';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { pl } from 'date-fns/locale';

interface ProductionHistory {
  id: string;
  order_number: string;
  client_name: string;
  products: any[];
  status: string;
  created_at: string;
  completed_at: string;
  production_time: number;
  material_usage: number;
  materials: any[];
  assigned_to?: string;
  notes?: string;
}

interface ProductionStats {
  totalOrders: number;
  completedOrders: number;
  averageProductionTime: number;
  averageMaterialUsage: number;
  totalMaterialsSaved: number;
}

export const ProductionHistory: React.FC = () => {
  const [history, setHistory] = useState<ProductionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ProductionStats | null>(null);
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');

  // Pobierz historię z bazy
  useEffect(() => {
    fetchHistory();
    fetchStats();
  }, [dateRange]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('production_orders')
        .select('*')
        .eq('status', 'completed')
        .gte('completed_at', dateRange.from.toISOString())
        .lte('completed_at', dateRange.to.toISOString())
        .order('completed_at', { ascending: false });

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Błąd pobierania historii:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        .from('production_orders')
        .select('*')
        .gte('created_at', dateRange.from.toISOString())
        .lte('created_at', dateRange.to.toISOString());

      if (error) throw error;

      if (data) {
        const completed = data.filter(o => o.status === 'completed');
        const totalTime = completed.reduce((sum, o) => sum + (o.production_time || 0), 0);
        const totalUsage = completed.reduce((sum, o) => sum + (o.material_usage || 0), 0);
        
        setStats({
          totalOrders: data.length,
          completedOrders: completed.length,
          averageProductionTime: completed.length > 0 ? totalTime / completed.length : 0,
          averageMaterialUsage: completed.length > 0 ? totalUsage / completed.length : 0,
          totalMaterialsSaved: completed.length * 100 - totalUsage // przykładowe obliczenie
        });
      }
    } catch (error) {
      console.error('Błąd pobierania statystyk:', error);
    }
  };

  // Zmień okres
  const changePeriod = (period: typeof selectedPeriod) => {
    setSelectedPeriod(period);
    const now = new Date();
    let from = new Date();

    switch (period) {
      case 'week':
        from = subDays(now, 7);
        break;
      case 'month':
        from = startOfMonth(now);
        break;
      case 'quarter':
        from = subDays(now, 90);
        break;
      case 'year':
        from = new Date(now.getFullYear(), 0, 1);
        break;
    }

    setDateRange({ from, to: now });
  };

  // Eksportuj do CSV
  const exportToCSV = () => {
    const headers = ['Nr zlecenia', 'Klient', 'Data realizacji', 'Czas produkcji', 'Wykorzystanie materiału'];
    const rows = history.map(h => [
      h.order_number,
      h.client_name,
      format(new Date(h.completed_at), 'dd.MM.yyyy'),
      `${h.production_time || 0} min`,
      `${h.material_usage || 0}%`
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `historia_produkcji_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  // Oblicz czas produkcji
  const formatProductionTime = (minutes: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  // Filtrowana historia
  const filteredHistory = history.filter(item => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        item.order_number.toLowerCase().includes(search) ||
        item.client_name.toLowerCase().includes(search) ||
        item.notes?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Statystyki */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-zinc-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Wszystkie zlecenia</span>
              <Package className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-white">{stats.totalOrders}</p>
            <p className="text-xs text-gray-500 mt-1">w wybranym okresie</p>
          </div>

          <div className="bg-zinc-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Zrealizowane</span>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-white">{stats.completedOrders}</p>
            <p className="text-xs text-green-400 mt-1">
              {stats.totalOrders > 0 ? Math.round((stats.completedOrders / stats.totalOrders) * 100) : 0}% realizacji
            </p>
          </div>

          <div className="bg-zinc-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Śr. czas produkcji</span>
              <Clock className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-white">
              {formatProductionTime(Math.round(stats.averageProductionTime))}
            </p>
            <p className="text-xs text-gray-500 mt-1">na zlecenie</p>
          </div>

          <div className="bg-zinc-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Wykorzystanie mat.</span>
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-white">
              {stats.averageMaterialUsage.toFixed(1)}%
            </p>
            <p className="text-xs text-green-400 mt-1">
              oszczędność {(100 - stats.averageMaterialUsage).toFixed(1)}%
            </p>
          </div>
        </div>
      )}

      {/* Filtry i akcje */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex items-center gap-4 w-full lg:w-auto">
          {/* Wyszukiwarka */}
          <div className="relative flex-1 lg:flex-initial">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Szukaj w historii..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white w-full lg:w-64 focus:border-orange-500 focus:outline-none"
            />
          </div>

          {/* Wybór okresu */}
          <div className="flex gap-2">
            {(['week', 'month', 'quarter', 'year'] as const).map(period => (
              <button
                key={period}
                onClick={() => changePeriod(period)}
                className={`px-3 py-1 rounded-lg text-sm transition-all ${
                  selectedPeriod === period 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-zinc-700 text-gray-400 hover:bg-zinc-600'
                }`}
              >
                {period === 'week' && 'Tydzień'}
                {period === 'month' && 'Miesiąc'}
                {period === 'quarter' && 'Kwartał'}
                {period === 'year' && 'Rok'}
              </button>
            ))}
          </div>
        </div>

        {/* Eksport */}
        <button
          onClick={exportToCSV}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Eksportuj CSV
        </button>
      </div>

      {/* Tabela historii */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      ) : filteredHistory.length === 0 ? (
        <div className="bg-zinc-700 rounded-lg p-12 text-center">
          <BarChart2 className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Brak historii</h3>
          <p className="text-gray-400">
            {searchTerm ? 'Nie znaleziono zleceń spełniających kryteria' : 'Brak zrealizowanych zleceń w wybranym okresie'}
          </p>
        </div>
      ) : (
        <div className="bg-zinc-700 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-600">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Nr zlecenia</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Klient</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Data realizacji</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Produkty</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Czas produkcji</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Wykorzystanie</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map(item => (
                  <tr key={item.id} className="border-b border-zinc-600 hover:bg-zinc-600/50 transition-all">
                    <td className="py-3 px-4 text-white font-medium">
                      #{item.order_number}
                    </td>
                    <td className="py-3 px-4 text-gray-300">
                      {item.client_name}
                    </td>
                    <td className="py-3 px-4 text-gray-300">
                      {format(new Date(item.completed_at), 'dd.MM.yyyy HH:mm', { locale: pl })}
                    </td>
                    <td className="py-3 px-4 text-gray-300">
                      {item.products?.length || 0} szt.
                    </td>
                    <td className="py-3 px-4 text-gray-300">
                      {formatProductionTime(item.production_time)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`font-medium ${
                        item.material_usage >= 90 ? 'text-green-400' : 
                        item.material_usage >= 80 ? 'text-yellow-400' : 
                        'text-red-400'
                      }`}>
                        {item.material_usage || 0}%
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm px-3 py-1 rounded-full bg-green-500/20 text-green-400">
                        Zrealizowane
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button className="text-gray-400 hover:text-white transition-all">
                        <FileText className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};